import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Stack,
  Alert,
  InputAdornment,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

import { useAuth } from '../context/AuthContext';
import { useBranding } from '../theme/BrandColorContext';
import api from '../api/client';

function Login() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { login, user, logout } = useAuth();
  const { setBranding } = useBranding();
  const [email, setEmail] = useState('');
  const hostname = window.location.hostname || '';
  const isConsole = hostname.startsWith('console.');
  const isPanel = hostname.startsWith('panel.');
  const [password, setPassword] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setBranding({
      primaryColor: null,
      accentColor: null,
      fontFamily: null,
      fontUrl: null,
      logo: null,
      slogan: null,
      fontSizeScale: 1,
    });
  }, [setBranding]);

  useEffect(() => {
    if (!user) return;
    if (isConsole) {
      if (user.role === 'platformadmin') {
        navigate('/console/clients', { replace: true });
      } else {
        logout();
      }
      return;
    }
    if (isPanel && user.role === 'platformadmin') {
      // Operator should never stay on panel login
      const consoleHostname = hostname.replace('panel.', 'console.');
      window.location.replace(`${window.location.protocol}//${consoleHostname}/console/clients`);
    }
  }, [user, isConsole, isPanel, hostname, navigate, logout]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setError('');

    try {
      const account = await api.post('/api/auth/login', { email: email.trim(), password });
      if (isConsole && account.user.role !== 'platformadmin') {
        setError('Konsola jest tylko dla operatorów platformy.');
        return;
      }
      if (isPanel && account.user.role === 'platformadmin') {
        setError('Operatorzy logują się na console.mymotivio.com');
        return;
      }
      if (isPanel && account.user.role === 'employee') {
        setError('Pracownicy szeregowi logują się przez aplikację mobilną.');
        return;
      }
      login(account);
      setSent(true);
    } catch (err) {
      setError(err.body?.error || err.message || 'Błąd logowania');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: theme.tokens?.semantic?.surface?.canvas ?? '#F7F6F3',
        p: { xs: 2, sm: 3 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: 500,
          height: 500,
          borderRadius: '50%',
          bgcolor: theme.tokens?.semantic?.surface?.brandTint ?? 'primary.50',
          opacity: 0.5,
          filter: 'blur(80px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-15%',
          left: '-5%',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${theme.palette.primary.main}10 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <Card
        sx={{
          width: '100%',
          maxWidth: 440,
          borderRadius: `${theme.tokens?.primitive?.radius?.hero ?? 30}px`,
          boxShadow: theme.tokens?.semantic?.shadow?.float,
          border: `1px solid ${theme.palette.divider}`,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              component="img"
              src="/logo.png"
              alt="Challenges"
              sx={{
                width: 56,
                height: 56,
                borderRadius: `${theme.tokens?.primitive?.radius?.control ?? 12}px`,
                objectFit: 'cover',
                mx: 'auto',
                mb: 2.5,
                display: 'block',
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontFamily: theme.typography.h1.fontFamily,
                fontWeight: 750,
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
                letterSpacing: '-0.02em',
              }}
            >
              {isConsole ? 'Konsola operatora' : 'Witaj ponownie'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {isConsole
                ? 'Zaloguj się do panelu operatora platformy'
                : 'Zaloguj się do panelu Engagement Challenges'}
            </Typography>
          </Box>

          {sent ? (
            <Alert
              severity="success"
              sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.control ?? 12}px`, mb: 2 }}
            >
              Zalogowano jako <strong>{email}</strong>. Przekierowanie...
            </Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                {error && (
                  <Alert severity="error" sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.control ?? 12}px` }}>
                    {error}
                  </Alert>
                )}
                <TextField
                  label="E-mail firmowy"
                  type="email"
                  fullWidth
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jan@firma.pl"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlinedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Hasło"
                  type="password"
                  fullWidth
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{
                    py: 1.5,
                    borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px`,
                    fontWeight: 700,
                    fontSize: '0.9375rem',
                  }}
                >
                  Zaloguj się
                </Button>
              </Stack>
            </form>
          )}

        </CardContent>
      </Card>
    </Box>
  );
}

export default Login;
